// Track consecutive failures using sessionStorage
const CONSECUTIVE_FAILURES_KEY = 'chunk_load_consecutive_failures';
const LAST_FAILURE_TIME_KEY = 'chunk_load_last_failure_time';
const MAX_CONSECUTIVE_FAILURES = 2;
const MAX_TIME_BETWEEN_FAILURES_MS = 45000; // 45 seconds

const isReduxStoreCorrupted = () => {
  try {
    const persistRoot = localStorage.getItem('persist:root');
    if (!persistRoot) return false;

    const rootState = JSON.parse(persistRoot);

    const knownReducers = ['theme', 'auth', 'sidebar', 'rtl'];
    const isCorrupted = knownReducers.some((reducer) => {
      if (!rootState[reducer]) return false;
      try {
        JSON.parse(rootState[reducer]);
        return false;
      } catch (e) {
        console.error(`Redux store corruption detected in ${reducer}:`, e);
        return true;
      }
    });

    if (isCorrupted) {
      console.warn('Encrypted/corrupted Redux store detected, clearing storage...');
      clearAllBrowserStorage();
      window.location.reload(true);
    }

    return isCorrupted;
  } catch (e) {
    console.error('Error checking Redux store corruption:', e);
    console.warn('Critical store corruption detected, clearing storage...');
    clearAllBrowserStorage();
    window.location.reload(true);
    return true;
  }
};

const clearAllBrowserStorage = async () => {
  try {
    const wasStoreCorrupted = isReduxStoreCorrupted();

    localStorage.clear();

    const tempFailures = sessionStorage.getItem(CONSECUTIVE_FAILURES_KEY);
    const tempTime = sessionStorage.getItem(LAST_FAILURE_TIME_KEY);
    sessionStorage.clear();

    document.cookie.split(';').forEach((cookie) => {
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();

      const clearCookie = (domain = '') => {
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/${domain}`;
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;${domain}`;
      };

      clearCookie();
      clearCookie(`domain=${window.location.hostname}`);
      clearCookie(`domain=.${window.location.hostname}`);
    });

    if ('caches' in window) {
      try {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map((cacheName) => caches.delete(cacheName)));
        console.log('Cleared all caches:', cacheNames);
      } catch (e) {
        console.error('Failed to clear cache:', e);
      }
    }

    if ('indexedDB' in window) {
      try {
        const databases = await window.indexedDB.databases();
        const deletePromises = databases.map((db) => {
          return new Promise((resolve) => {
            const deleteReq = window.indexedDB.deleteDatabase(db.name);
            deleteReq.onsuccess = () => resolve();
            deleteReq.onerror = () => resolve();
            deleteReq.onblocked = () => resolve();
          });
        });
        await Promise.all(deletePromises);
        console.log(
          'Cleared IndexedDB databases:',
          databases.map((db) => db.name),
        );
      } catch (e) {
        console.error('Failed to clear IndexedDB:', e);
      }
    }

    if ('openDatabase' in window) {
      try {
        console.log('Web SQL detected but cannot be cleared programmatically');
      } catch (e) {
        console.error('Failed to clear Web SQL:', e);
      }
    }

    console.log('All browser storage cleared successfully');
  } catch (error) {
    console.error('Error during storage cleanup:', error);
  }
};

const resetFailureTracking = () => {
  try {
    sessionStorage.setItem(CONSECUTIVE_FAILURES_KEY, '0');
    sessionStorage.removeItem(LAST_FAILURE_TIME_KEY);
  } catch (e) {
    console.error('Failed to reset failure tracking:', e);
  }
};

const isChunkLoadError = (error) => {
  if (!error || typeof error.message !== 'string') return false;

  if (isReduxStoreCorrupted()) {
    console.warn('Redux store corruption detected');
    return true;
  }

  const errorPatterns = [
    'Failed to fetch dynamically imported module',
    'Loading chunk',
    'ChunkLoadError',
    'Loading CSS chunk',
    'Failed to import',
    'NetworkError',
    'fetch',
    'Unexpected token',
    'JSON.parse',
    'undefined is not an object',
    'Cannot read property',
  ];

  return errorPatterns.some(
    (pattern) =>
      error.message.includes(pattern) || error.name === pattern || (error.code && error.code.includes('NETWORK')),
  );
};

export const handleDynamicImportError = async (error) => {
  console.error('Dynamic import error:', error);

  const isStoreCorrupted = isReduxStoreCorrupted();

  if (isStoreCorrupted || isChunkLoadError(error)) {
    try {
      const currentTime = Date.now();
      const lastFailureTime = parseInt(sessionStorage.getItem(LAST_FAILURE_TIME_KEY) || '0');
      const timeSinceLastFailure = currentTime - lastFailureTime;

      if (isStoreCorrupted) {
        console.warn('Redux store corruption detected, performing immediate cleanup...');
        await clearAllBrowserStorage();
        resetFailureTracking();
        window.location.reload(true);
        return;
      }

      let consecutiveFailures = parseInt(sessionStorage.getItem(CONSECUTIVE_FAILURES_KEY) || '0');

      if (timeSinceLastFailure > MAX_TIME_BETWEEN_FAILURES_MS) {
        consecutiveFailures = 0;
      }

      consecutiveFailures++;
      sessionStorage.setItem(CONSECUTIVE_FAILURES_KEY, consecutiveFailures.toString());
      sessionStorage.setItem(LAST_FAILURE_TIME_KEY, currentTime.toString());

      console.warn(`Chunk load error detected (attempt ${consecutiveFailures}/${MAX_CONSECUTIVE_FAILURES})`);

      if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES && timeSinceLastFailure <= MAX_TIME_BETWEEN_FAILURES_MS) {
        console.warn(
          // eslint-disable-next-line max-len
          `Multiple consecutive chunk load failures detected within ${MAX_TIME_BETWEEN_FAILURES_MS / 1000} seconds. Performing complete cleanup...`,
        );

        await clearAllBrowserStorage();
        resetFailureTracking();

        window.location.reload(true);
      } else {
        console.warn('Attempting page reload to recover from chunk load error...');
        window.location.reload();
      }
    } catch (handlingError) {
      console.error('Error while handling dynamic import error:', handlingError);
      window.location.reload(true);
    }
  } else {
    resetFailureTracking();
    throw error;
  }
};

export const checkAndClearEncryptedData = () => {
  try {
    const persistRoot = localStorage.getItem('persist:root');
    if (!persistRoot) return false;

    let shouldClear = false;

    try {
      const rootState = JSON.parse(persistRoot);
      const knownReducers = ['theme', 'auth', 'sidebar', 'rtl'];
      knownReducers.forEach((reducer) => {
        if (rootState[reducer]) {
          try {
            const stateData = JSON.parse(rootState[reducer]);
            if (
              typeof stateData === 'string' &&
              (stateData.includes('==') || stateData.includes('encrypted') || /[^A-Za-z0-9+/=]/.test(stateData))
            ) {
              shouldClear = true;
              console.warn(`Encrypted data detected in ${reducer}`);
            }
          } catch (e) {
            shouldClear = true;
            console.warn(`Corrupted data detected in ${reducer}`);
          }
        }
      });
    } catch (e) {
      shouldClear = true;
      console.warn('Corrupted root state detected');
    }

    if (shouldClear) {
      console.warn('Clearing encrypted/corrupted data...');
      clearAllBrowserStorage();
      window.location.reload(true);
      return true;
    }

    return false;
  } catch (e) {
    console.error('Error during encryption check:', e);
    return false;
  }
};
